import { DispatcherData, DispatcherDispatchRecord, DispatcherStaffing } from './dispatcher_types';

export type DispatcherSavePayload = {
  publish: boolean;
  date: string;
  region_id: number;
  drivers_required: number;
  non_drivers_required: number;
  batch: Array<{
    _destroy?: boolean;
    region_id: number;
    lock_version?: number;
    id: number | null;
    arrival?: Date | string | null;
    break_at?: Date | string | null;
    mover_notes?: string | null;
    warehouse_notes?: string | null;
    addons?: string[];
    load_out?: string | null;
    depot_id?: number | string | null;
    warehouse_id?: number | string | null;
    orders_attributes: Array<{ id: number; dispatch_offset?: number }>;
    assignments_attributes: Array<{
      _destroy?: boolean;
      id?: number | string | null;
      role?: string;
      user_id?: number | string;
    }>;
    vehicle_preferences_attributes: Array<{
      id?: number | string | null;
      vehicle_type_id: number | string;
      quantity?: number;
      _destroy?: boolean;
    }>;
    vehicle_recipe_attributes: {
      id?: number | string | null;
      min_quantity?: number;
      max_quantity?: number;
      minimum_cuft?: number;
      allowed_vehicle_type_ids?: number[];
      notes?: string | null;
    } | null;
  }>;
};

export const hasPublishedDispatches = (dispatches: DispatcherDispatchRecord[]) =>
  dispatches.some((dispatch) => !!dispatch.published_at);

const mapAssignmentAttributes = (assignments: DispatcherDispatchRecord['assignments'] = []) =>
  (assignments ?? [])
    .filter((assignment) => {
      if (assignment._destroy) {
        return assignment.id != null && assignment.user?.id != null;
      }

      return assignment.user?.id != null && assignment.role;
    })
    .map((assignment) => ({
      _destroy: assignment._destroy,
      id: assignment.id,
      role: assignment.role,
      user_id: assignment.user?.id,
    }));

const mapDispatchBatch = (dispatch: DispatcherDispatchRecord, regionId: number) => ({
  _destroy: dispatch._destroy,
  region_id: regionId,
  lock_version: dispatch.lock_version,
  id: dispatch.id,
  arrival: dispatch.arrival,
  break_at: dispatch.break_at,
  mover_notes: dispatch.mover_notes,
  warehouse_notes: dispatch.warehouse_notes,
  addons: dispatch.addons,
  load_out: dispatch.load_out,
  depot_id: dispatch.depot?.id ?? null,
  warehouse_id: dispatch.warehouse?.id ?? null,
  orders_attributes: dispatch.orders.map(({ id, dispatch_offset: dispatchOffset }) => ({
    id,
    dispatch_offset: dispatchOffset,
  })),
  assignments_attributes: mapAssignmentAttributes(dispatch.assignments),
  vehicle_preferences_attributes: (dispatch.vehicle_preferences ?? []).map((preference) => ({
    id: preference.id,
    vehicle_type_id: preference.vehicle_type_id,
    quantity: preference.quantity,
    _destroy: preference._destroy,
  })),
  vehicle_recipe_attributes: dispatch.vehicle_recipe
    ? {
        id: dispatch.vehicle_recipe.id,
        min_quantity: dispatch.vehicle_recipe.min_quantity,
        max_quantity: dispatch.vehicle_recipe.max_quantity,
        minimum_cuft: dispatch.vehicle_recipe.minimum_cuft,
        allowed_vehicle_type_ids: dispatch.vehicle_recipe.allowed_vehicle_type_ids,
        notes: dispatch.vehicle_recipe.notes,
      }
    : null,
});

export const buildDispatcherSavePayload = ({
  publish,
  date,
  regionId,
  dispatches,
  staffing,
}: {
  publish: boolean;
  date: string;
  regionId: number;
  dispatches: DispatcherDispatchRecord[];
  staffing: DispatcherStaffing;
}): DispatcherSavePayload => ({
  publish,
  date,
  region_id: regionId,
  drivers_required: staffing.driversRequired,
  non_drivers_required: staffing.nonDriversRequired,
  batch: dispatches.map((dispatch) => mapDispatchBatch(dispatch, regionId)),
});

export const buildSavePayloadFromEditorData = (
  editorData: DispatcherData,
  publish: boolean,
  date: string,
  regionId: number,
  staffing: DispatcherStaffing,
): DispatcherSavePayload =>
  buildDispatcherSavePayload({
    publish,
    date,
    regionId,
    dispatches: editorData.dispatches,
    staffing,
  });
